import { useParams } from "react-router-dom";
import {
  useGetProxyEventDetailsQuery,
  useGetProxyTimelineTrendQuery,
} from "../../../../../../../../../../store/proxyEvent";
import { useMemo } from "react";
import dayjs from "dayjs";
import { IGraphData } from "../../../Organisms/ProxyDetailGraph/ProxyDetailGraph";
import {
  GraphIconType,
  IGraphDotStat,
} from "../../../../../../../../../../components/Graphs/types";
import { GRAPH_DATE_FORMAT } from "../../../../../../../../../../components/Graphs/constants";

interface IProxyGraphDotStat extends IGraphDotStat {
  shares: number;
  shareholders: number;
}

interface IProxyGraphDotStats {
  [key: string]: IProxyGraphDotStat;
}

export const useGraphData = () => {
  const { proxyEventId } = useParams() as { proxyEventId: string };
  const { data: timelineData = [], isLoading: isTimelineLoading } =
    useGetProxyTimelineTrendQuery(proxyEventId);

  const { data: detailsData, isLoading: isDetailsLoading } =
    useGetProxyEventDetailsQuery(proxyEventId);

  const isLoading = isTimelineLoading || isDetailsLoading;

  /** CORE LINE GRAPH DATA **/
  // parse graph data to be able to be displayed in graph
  const graphData = useMemo(() => {
    if (isLoading || !timelineData?.length) {
      return [];
    }

    // convert individual day data to cumulative, and format date name
    const chartData = timelineData.reduce((acc, cur) => {
      const prevShareholders = acc?.[acc.length - 1]?.shareholders || 0;
      const prevShares = acc?.[acc.length - 1]?.shares || 0;
      acc.push({
        shareholders: prevShareholders + (cur?.customerCount || 0),
        shares: prevShares + parseFloat(cur?.totalSharesVoted || "0"),
        name: dayjs(cur.date).format(GRAPH_DATE_FORMAT),
      });

      return acc;
    }, [] as IGraphData[]);

    const meetingDate = detailsData?.meeting?.meetingDate as string;
    const lastDay = timelineData[timelineData.length - 1].date;
    // days between today and meeting date
    const daysToAdd = dayjs(meetingDate).diff(dayjs(lastDay), "day");
    // add blank days from today -> meeting date
    for (let i = 0; i < daysToAdd; i++) {
      chartData.push({
        name: dayjs(lastDay).add(i, "day").format(GRAPH_DATE_FORMAT),
        shares: undefined,
        shareholders: undefined,
      });
    }

    // prepend day before voting starts for chart starting point
    const dayBeforeVotingStart = dayjs(chartData[0].name)
      .subtract(1, "day")
      .format(GRAPH_DATE_FORMAT);
    chartData.unshift({
      name: dayBeforeVotingStart,
      shares: 0,
      shareholders: 0,
    });

    return chartData;
  }, [isLoading, timelineData]);

  const initialMailingShortDate = dayjs(detailsData?.approvedAt).format(
    GRAPH_DATE_FORMAT
  );

  /** CONVERT ALL MAILINGS TO AN OBJECT FOR PARSING **/
  const mailings = useMemo(
    () =>
      !isLoading && detailsData
        ? {
            [initialMailingShortDate]: {
              name: "Initial mailing",
              type: GraphIconType.Additional,
            },
            ...detailsData?.additionalMailings?.reduce(
              (acc, cur, idx) => (
                (acc[dayjs(cur.approvedAt).format(GRAPH_DATE_FORMAT)] = {
                  name: `Additional mailing ${idx + 1}`,
                  type: GraphIconType.Additional,
                }),
                acc
              ),
              {}
            ),
            ...detailsData?.voteSolicitations?.reduce(
              (acc, cur, idx) => (
                (acc[dayjs(cur.scheduledSendAt).format(GRAPH_DATE_FORMAT)] = {
                  name: `Voting message ${idx + 1}`,
                  type: GraphIconType.ProxyMessage,
                }),
                acc
              ),
              {}
            ),
          }
        : {},
    [isLoading, detailsData]
  );

  /** Get the cumulative stats between each mailing **/
  const { graphEventStats } = useMemo(
    () =>
      timelineData.reduce(
        (acc, cur) => {
          const curShareholders = cur.customerCount || 0;
          const curShares = parseFloat(cur.totalSharesVoted || "0");
          const curName = dayjs(cur.date).format(GRAPH_DATE_FORMAT);
          const curNameWithYear = dayjs(cur.date).format(
            `${GRAPH_DATE_FORMAT}, YYYY`
          );
          if (mailings[curName]) {
            acc.graphEventStats[curName] = {
              shares: curShares,
              shareholders: curShareholders,
              range: `${acc.curDate} - ${curNameWithYear}`,
              type: mailings[curName].type,
              name: mailings[curName].name,
            };
            acc.curDate = curName;
          } else if (acc.graphEventStats[acc.curDate]) {
            (acc.graphEventStats[acc.curDate].shares as number) += curShares;
            (acc.graphEventStats[acc.curDate].shareholders as number) +=
              curShareholders as number;
            acc.graphEventStats[
              acc.curDate
            ].range = `${acc.curDate} - ${curNameWithYear}`;
          }

          return acc;
        },
        { graphEventStats: {}, curDate: initialMailingShortDate } as {
          graphEventStats: IProxyGraphDotStats;
          curDate: string;
        }
      ),
    [graphData]
  );

  const totalShareholdersInGraph = useMemo(
    () => Math.max(...graphData.map((o) => o.shareholders || 0)),
    [graphData]
  );
  return {
    graphData,
    totalShareholdersInGraph,
    graphEventStats,
    isLoading,
    fullTimelineData: timelineData,
  };
};
